import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export function DocDeleteButton({ doc }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success(t("doc.deleted"));
      qc.invalidateQueries({ queryKey: ["documents"] });
      qc.invalidateQueries({ queryKey: ["cases"] });
      qc.invalidateQueries({ queryKey: ["case"] });
    } catch (err) {
      toast.error(apiError(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          data-testid={`document-delete-${doc.id}`}
          onClick={(e) => e.stopPropagation()}
          className="rounded-full p-2 text-[#C03A2B] transition-colors hover:bg-[#FDF2F0] active:scale-95"
          aria-label={t("doc.delete")}
        >
          <Trash2 size={17} />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="document-delete-dialog" className="max-w-sm rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display">{t("doc.deleteConfirmTitle")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("doc.deleteConfirmText").replace("{name}", doc.original_filename)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="document-delete-cancel" className="rounded-xl">{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            data-testid="document-delete-confirm"
            disabled={busy}
            onClick={remove}
            className="rounded-xl bg-[#C03A2B] hover:bg-[#a93124]"
          >
            {t("doc.delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
